/**
 * Cookie utility functions for managing browser cookies
 */

export interface SetCookieOptions {
    days?: number;
    domain?: string;
    secure?: boolean;
    sameSite?: 'Strict' | 'Lax' | 'None';
    path?: string;
}

/**
 * Set a cookie with specified options
 * @param name Cookie name
 * @param value Cookie value
 * @param options Cookie configuration options
 */
export function setCookie(
    name: string,
    value: string,
    options: SetCookieOptions = {}
): void {
    if (typeof window === 'undefined') return;

    const {
        days = 730, // 2 years default
        domain,
        secure = true,
        sameSite = 'Lax',
        path = '/',
    } = options;

    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

    // Set expiration
    if (days) {
        const date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        cookieString += `; expires=${date.toUTCString()}`;
    }

    // Set path
    cookieString += `; path=${path}`;

    // Set domain (e.g., .muzology.com for all subdomains)
    if (domain) {
        cookieString += `; domain=${domain}`;
    }

    // Set secure flag
    if (secure) {
        cookieString += '; secure';
    }

    // Set SameSite
    cookieString += `; SameSite=${sameSite}`;

    document.cookie = cookieString;
}

/**
 * Get a cookie value by name
 * @param name Cookie name
 * @returns Cookie value or null if not found
 */
export function getCookie(name: string): string | null {
    if (typeof window === 'undefined') return null;

    const nameEQ = encodeURIComponent(name) + '=';
    const cookies = document.cookie.split(';');

    for (let i = 0; i < cookies.length; i++) {
        let cookie = cookies[i];
        while (cookie.charAt(0) === ' ') {
            cookie = cookie.substring(1, cookie.length);
        }
        if (cookie.indexOf(nameEQ) === 0) {
            return decodeURIComponent(
                cookie.substring(nameEQ.length, cookie.length)
            );
        }
    }

    return null;
}

/**
 * Delete a cookie by name
 * @param name Cookie name
 * @param domain Cookie domain (must match the domain used when setting)
 */
export function deleteCookie(name: string, domain?: string): void {
    setCookie(name, '', {
        days: -1,
        domain,
    });
}

/**
 * Check if a cookie exists
 * @param name Cookie name
 * @returns True if cookie exists
 */
export function hasCookie(name: string): boolean {
    return getCookie(name) !== null;
}
